import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack,
  useTheme,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Apple,
  Android,
  PlayArrow,
  TrendingUp,
  Security,
  Speed,
} from '@mui/icons-material';

const HeroSection: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
        color: 'white',
        py: { xs: 10, md: 16 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.5,
        },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            {/* Badge */}
            <Chip
              icon={<TrendingUp />}
              label="Trusted by 50K+ Users"
              sx={{
                bgcolor: 'rgba(102, 126, 234, 0.2)',
                color: '#667eea',
                mb: 3,
                border: '1px solid rgba(102, 126, 234, 0.3)',
                '& .MuiChip-icon': {
                  color: '#667eea',
                },
              }}
            />
            
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '4rem' },
                lineHeight: 1.1,
                color: 'white',
              }}
            >
              Your Smart Metro
              <br />
              <span style={{ 
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Companion
              </span>
            </Typography>
            
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 300,
                lineHeight: 1.4,
              }}
            >
              Navigate metro networks with AI-powered route planning, real-time updates, 
              and seamless station discovery.
            </Typography>
            
            {/* Feature highlights */}
            <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ width: 24, height: 24, bgcolor: 'rgba(102, 126, 234, 0.2)', mr: 1, border: '1px solid rgba(102, 126, 234, 0.3)' }}>
                  <Speed sx={{ fontSize: 14, color: '#667eea' }} />
                </Avatar>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Real-time Updates
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ width: 24, height: 24, bgcolor: 'rgba(255, 152, 0, 0.2)', mr: 1, border: '1px solid rgba(255, 152, 0, 0.3)' }}>
                  <Security sx={{ fontSize: 14, color: '#FF9800' }} />
                </Avatar>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Secure & Private
                </Typography>
              </Box>
            </Stack>
            
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ mb: 4 }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<Apple />}
                sx={{
                  bgcolor: '#667eea',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#5a6fd8',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
                component="a"
                href="https://apps.apple.com/app/metrorahi"
                target="_blank"
              >
                Download on App Store
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Android />}
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#5a6fd8',
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.2)',
                  },
                  transition: 'all 0.3s ease',
                }}
                component="a"
                href="https://play.google.com/store/apps/details?id=com.metrorahi.app"
                target="_blank"
              >
                Get it on Google Play
              </Button>
            </Stack>
            
            {/* Demo button */}
            <Button
              variant="text"
              startIcon={<PlayArrow />}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Watch Demo
            </Button>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                position: 'relative',
              }}
            >
              {/* Phone mockup */}
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: 280, md: 320 },
                  height: { xs: 560, md: 640 },
                  background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
                  borderRadius: 4,
                  padding: 2,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    height: '90%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                    opacity: 0.1,
                  },
                }}
              >
                {/* Screen content */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    textAlign: 'center',
                    p: 3,
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    MetroRahi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Smart Metro Navigation
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#667eea', opacity: 0.8 }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.3)' }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.3)' }} />
                  </Box>
                </Box>
              </Box>
              
              {/* Floating elements */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '20%',
                  right: '10%',
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
                  animation: 'float 3s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-10px)' },
                  },
                }}
              >
                <TrendingUp sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '30%',
                  left: '5%',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'linear-gradient(45deg, #4CAF50, #45a049)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 25px rgba(76,175,80,0.4)',
                  animation: 'float 3s ease-in-out infinite 1s',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-8px)' },
                  },
                }}
              >
                <Speed sx={{ color: 'white', fontSize: 18 }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection; 